import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import jsPDF from "jspdf";
import { Award, Wand2, Loader2, Sparkles, Download, Trash2, ChevronDown } from "lucide-react";

import { api } from "@/api/client";
import { useLang } from "@/context/LangContext";
import { Input } from "@/components/ui/input";

const DEFAULT_ACH_ES = "Experto Canales";
const DEFAULT_ACH_EN = "Canales Expert";

/**
 * AI-powered certificate designer — admin only.
 * Pipeline:
 *  1. Admin picks employee + achievement + design template.
 *  2. Backend calls Nano Banana with the template's style brief.
 *  3. Frontend overlays the employee name + achievement + Canales logo/date on top.
 *  4. Admin downloads the finished landscape PDF.
 */
export default function CertificateDesigner() {
  const { lang } = useLang();
  const [templates, setTemplates] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [history, setHistory] = useState([]);

  const [employeeName, setEmployeeName] = useState("");
  const [selectedTpl, setSelectedTpl] = useState("gold_classic");
  const [achievementEs, setAchievementEs] = useState(DEFAULT_ACH_ES);
  const [achievementEn, setAchievementEn] = useState(DEFAULT_ACH_EN);

  const [category, setCategory] = useState("professional");
  const [fontReady, setFontReady] = useState(false);
  const fontB64Ref = useRef(null);

  const [generating, setGenerating] = useState(false);
  const [preview, setPreview] = useState(null); // {key, template, data, mime_type}

  useEffect(() => {
    (async () => {
      try {
        const [tRes, eRes, hRes] = await Promise.all([
          api.get("/admin/certificate/templates"),
          api.get("/admin/employees"),
          api.get("/admin/certificate/history"),
        ]);
        setTemplates(tRes.data || []);
        setEmployees(eRes.data || []);
        setHistory(hRes.data || []);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Error cargando datos");
      }
    })();
  }, []);

  const currentTpl = useMemo(
    () => templates.find((t) => t.key === selectedTpl) || null,
    [templates, selectedTpl]
  );

  const filteredTemplates = useMemo(
    () => templates.filter((t) => (t.category || "professional") === category),
    [templates, category]
  );

  const catOf = (key) => templates.find((t) => t.key === key)?.category || "professional";
  const proMode = catOf(preview?.template || selectedTpl) === "professional";

  const switchCategory = (c) => {
    setCategory(c);
    const list = templates.filter((t) => (t.category || "professional") === c);
    if (list.length && !list.some((t) => t.key === selectedTpl)) setSelectedTpl(list[0].key);
  };

  // Load the cursive font once (kept out of the main bundle via dynamic import).
  useEffect(() => {
    let cancelled = false;
    import("@/assets/greatVibes")
      .then(({ GREAT_VIBES_BASE64 }) => {
        if (cancelled) return;
        fontB64Ref.current = GREAT_VIBES_BASE64;
        if (!document.getElementById("gv-font-face")) {
          const style = document.createElement("style");
          style.id = "gv-font-face";
          style.textContent = `@font-face{font-family:'GreatVibes';src:url(data:font/ttf;base64,${GREAT_VIBES_BASE64}) format('truetype');font-display:swap;}`;
          document.head.appendChild(style);
        }
        setFontReady(true);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const generate = async () => {
    if (!employeeName.trim()) {
      toast.error(lang === "es" ? "Elige un vendedor" : "Pick a vendor");
      return;
    }
    setGenerating(true);
    setPreview(null);
    try {
      const { data } = await api.post("/admin/certificate/design", {
        template: selectedTpl,
        employee_name: employeeName.trim(),
        achievement_es: achievementEs,
        achievement_en: achievementEn,
      });
      setPreview(data);
      const h = await api.get("/admin/certificate/history");
      setHistory(h.data || []);
      toast.success(lang === "es" ? "¡Certificado listo!" : "Certificate ready!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error generando");
    } finally {
      setGenerating(false);
    }
  };

  const downloadPDF = async () => {
    if (!preview) return;
    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
    const W = doc.internal.pageSize.getWidth();
    const H = doc.internal.pageSize.getHeight();
    doc.addImage(
      `data:${preview.mime_type};base64,${preview.data}`,
      "PNG", 0, 0, W, H, undefined, "FAST"
    );
    const pro = catOf(preview.template) === "professional";
    const cx = W / 2;
    const today = new Date().toLocaleDateString(lang === "es" ? "es-MX" : "en-US", {
      year: "numeric", month: "long", day: "numeric",
    });

    if (pro) {
      // Register the cursive font for the recipient name.
      let scriptFont = "times", scriptStyle = "bolditalic", scriptSize = 52;
      try {
        const b64 = fontB64Ref.current || (await import("@/assets/greatVibes")).GREAT_VIBES_BASE64;
        doc.addFileToVFS("GreatVibes.ttf", b64);
        doc.addFont("GreatVibes.ttf", "GreatVibes", "normal");
        scriptFont = "GreatVibes"; scriptStyle = "normal"; scriptSize = 74;
      } catch { /* fallback to serif italic */ }
      const top = H * 0.30;
      // faint readability panel
      doc.setFillColor(255, 255, 255);
      doc.setGState(new doc.GState({ opacity: 0.32 }));
      doc.roundedRect(W * 0.13, H * 0.20, W * 0.74, H * 0.58, 14, 14, "F");
      doc.setGState(new doc.GState({ opacity: 1 }));
      // Title
      doc.setFont("times", "bold"); doc.setTextColor(13, 26, 63); doc.setFontSize(34);
      doc.text(lang === "es" ? "CERTIFICADO DE APRECIACIÓN" : "CERTIFICATE OF APPRECIATION",
        cx, top, { align: "center", charSpace: 4 });
      doc.setDrawColor(180, 150, 60); doc.setLineWidth(1.6);
      doc.line(cx - 80, top + 12, cx + 80, top + 12);
      // Subtitle
      doc.setFont("helvetica", "normal"); doc.setTextColor(90, 90, 90); doc.setFontSize(11);
      doc.text(lang === "es" ? "ESTE CERTIFICADO SE OTORGA CON ORGULLO A" : "THIS CERTIFICATE IS PROUDLY PRESENTED TO",
        cx, top + 42, { align: "center", charSpace: 1.5 });
      // Name (script)
      doc.setFont(scriptFont, scriptStyle); doc.setTextColor(17, 17, 17); doc.setFontSize(scriptSize);
      doc.text(employeeName, cx, top + 104, { align: "center" });
      doc.setDrawColor(70, 70, 70); doc.setLineWidth(0.6);
      doc.line(cx - 190, top + 120, cx + 190, top + 120);
      // Achievement
      doc.setFont("times", "bold"); doc.setTextColor(13, 26, 63); doc.setFontSize(16);
      doc.text(lang === "es" ? achievementEs : achievementEn, cx, top + 144, { align: "center", charSpace: 2 });
      // Date + signature
      doc.setFont("helvetica", "normal"); doc.setTextColor(60, 60, 60); doc.setFontSize(11);
      doc.setDrawColor(90, 90, 90); doc.setLineWidth(0.6);
      doc.line(cx - 250, H - 78, cx - 90, H - 78);
      doc.line(cx + 90, H - 78, cx + 250, H - 78);
      doc.text(today, cx - 170, H - 64, { align: "center" });
      doc.text(lang === "es" ? "Fecha" : "Date", cx - 170, H - 50, { align: "center" });
      doc.text(lang === "es" ? "Dirección Canales Furniture" : "Canales Furniture Mgmt", cx + 170, H - 64, { align: "center" });
      doc.text(lang === "es" ? "Firma" : "Signature", cx + 170, H - 50, { align: "center" });
    } else {
      // FUN layout — bold comic-style cream card.
      doc.setFillColor(255, 246, 217);
      doc.setGState(new doc.GState({ opacity: 0.55 }));
      const cardX = W * 0.15, cardY = H * 0.28, cardW = W * 0.7, cardH = H * 0.44;
      doc.roundedRect(cardX, cardY, cardW, cardH, 20, 20, "F");
      doc.setGState(new doc.GState({ opacity: 1 }));
      doc.setFont("times", "bold"); doc.setTextColor(13, 26, 63); doc.setFontSize(38);
      doc.text(lang === "es" ? "CERTIFICADO DE EXCELENCIA" : "CERTIFICATE OF EXCELLENCE",
        W / 2, cardY + 46, { align: "center", charSpace: 3 });
      doc.setFont("helvetica", "normal"); doc.setTextColor(80, 80, 80); doc.setFontSize(14);
      doc.text(lang === "es" ? "Este certificado se otorga con orgullo a" : "This is proudly presented to",
        W / 2, cardY + 90, { align: "center" });
      doc.setFont("times", "bolditalic"); doc.setTextColor(227, 0, 15); doc.setFontSize(52);
      doc.text(employeeName, W / 2, cardY + 150, { align: "center" });
      doc.setFont("helvetica", "bold"); doc.setTextColor(13, 26, 63); doc.setFontSize(20);
      doc.text(lang === "es" ? achievementEs : achievementEn, W / 2, cardY + 190, { align: "center", charSpace: 2 });
      doc.setFont("helvetica", "normal"); doc.setTextColor(60, 60, 60); doc.setFontSize(11);
      doc.text(today, W / 2 - 140, cardY + cardH - 30, { align: "center" });
      doc.text(lang === "es" ? "Dirección Canales Furniture" : "Canales Furniture Management",
        W / 2 + 140, cardY + cardH - 30, { align: "center" });
      doc.setDrawColor(13, 26, 63); doc.setLineWidth(0.6);
      doc.line(W / 2 - 200, cardY + cardH - 42, W / 2 - 80, cardY + cardH - 42);
      doc.line(W / 2 + 80, cardY + cardH - 42, W / 2 + 200, cardY + cardH - 42);
    }
    doc.save(`canales-certificado-${employeeName.replace(/\s+/g, "-")}.pdf`);
  };

  const deleteDesign = async (key) => {
    if (!window.confirm("¿Borrar este diseño del historial?")) return;
    try {
      await api.delete(`/admin/certificate/${key}`);
      setHistory((h) => h.filter((x) => x.key !== key));
      if (preview?.key === key) setPreview(null);
    } catch (e) {
      toast.error("Error borrando");
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
      data-testid="admin-cert-designer"
    >
      <div>
        <h1 className="font-display font-black text-4xl flex items-center gap-3">
          <Wand2 className="w-8 h-8 text-canales-red" />
          {lang === "es" ? "Diseñador de Certificados AI" : "AI Certificate Designer"}
        </h1>
        <p className="font-body text-neutral-700 mt-1 max-w-3xl">
          {lang === "es"
            ? "Solo el admin puede crear y descargar certificados. Nano Banana pinta el fondo con el estilo elegido y la app superpone nombre + logro."
            : "Only admins can create and download certificates. Nano Banana paints the background in the picked style; the app overlays the name + achievement."}
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* CONTROLS */}
        <div className="brutal p-5 bg-white space-y-4 lg:col-span-1">
          <div>
            <label className="text-xs font-display font-black uppercase tracking-widest text-canales-red">
              {lang === "es" ? "Vendedor" : "Vendor"}
            </label>
            <div className="relative mt-1">
              <Input
                data-testid="cert-designer-employee-input"
                list="cert-employees"
                value={employeeName}
                onChange={(e) => setEmployeeName(e.target.value)}
                placeholder={lang === "es" ? "Ej. Ana Perez" : "e.g. Ana Perez"}
                className="brutal-sm bg-white"
              />
              <datalist id="cert-employees">
                {employees.map((e) => (
                  <option key={e.employee_name} value={e.employee_name}>
                    {e.avg}% avg · {e.attempts} attempts
                  </option>
                ))}
              </datalist>
              <ChevronDown className="absolute right-2 top-3 w-4 h-4 text-neutral-400 pointer-events-none" />
            </div>
          </div>

          <div>
            <label className="text-xs font-display font-black uppercase tracking-widest text-canales-red">
              {lang === "es" ? "Logro (ES)" : "Achievement (ES)"}
            </label>
            <Input
              data-testid="cert-designer-achievement-es"
              value={achievementEs}
              onChange={(e) => setAchievementEs(e.target.value)}
              placeholder={DEFAULT_ACH_ES}
              className="brutal-sm mt-1 bg-white"
            />
          </div>

          <div>
            <label className="text-xs font-display font-black uppercase tracking-widest text-canales-red">
              {lang === "es" ? "Logro (EN)" : "Achievement (EN)"}
            </label>
            <Input
              data-testid="cert-designer-achievement-en"
              value={achievementEn}
              onChange={(e) => setAchievementEn(e.target.value)}
              placeholder={DEFAULT_ACH_EN}
              className="brutal-sm mt-1 bg-white"
            />
          </div>

          <div>
            <div className="text-xs font-display font-black uppercase tracking-widest text-canales-red mb-2">
              {lang === "es" ? "Estilo" : "Style"}
            </div>
            <div className="grid grid-cols-2 gap-2 mb-3">
              <button
                data-testid="cert-cat-professional"
                onClick={() => switchCategory("professional")}
                className={`brutal-sm px-3 py-2 text-sm font-display font-black transition-colors ${
                  category === "professional" ? "bg-canales-blue text-white" : "bg-white hover:bg-canales-cream"
                }`}
              >
                {lang === "es" ? "Profesionales" : "Professional"}
              </button>
              <button
                data-testid="cert-cat-fun"
                onClick={() => switchCategory("fun")}
                className={`brutal-sm px-3 py-2 text-sm font-display font-black transition-colors ${
                  category === "fun" ? "bg-canales-yellow text-black" : "bg-white hover:bg-canales-cream"
                }`}
              >
                {lang === "es" ? "Divertidos" : "Fun"}
              </button>
            </div>
            <div className="text-xs font-display font-black uppercase tracking-widest text-canales-red mb-2">
              {lang === "es" ? "Diseño" : "Design"}
            </div>
            <div className="grid grid-cols-2 gap-2">
              {filteredTemplates.map((t) => (
                <button
                  key={t.key}
                  data-testid={`cert-template-${t.key}`}
                  onClick={() => setSelectedTpl(t.key)}
                  className={`brutal-sm px-3 py-2 text-sm text-left font-display font-bold transition-colors ${
                    selectedTpl === t.key
                      ? "bg-canales-yellow"
                      : "bg-white hover:bg-canales-cream"
                  }`}
                >
                  {t[`label_${lang}`] || t.label_es}
                </button>
              ))}
            </div>
            {currentTpl && (
              <div className="mt-2 text-[11px] font-body text-neutral-500 line-clamp-3">
                {currentTpl.style?.slice(0, 200)}…
              </div>
            )}
          </div>

          <button
            data-testid="cert-designer-generate"
            onClick={generate}
            disabled={generating || !employeeName.trim()}
            className="w-full brutal-btn bg-canales-red text-white px-4 py-3 flex items-center justify-center gap-2 font-display font-black disabled:opacity-50"
          >
            {generating ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                {lang === "es" ? "Pintando…" : "Painting…"}
              </>
            ) : (
              <>
                <Sparkles className="w-5 h-5" />
                {lang === "es" ? "Generar certificado" : "Generate certificate"}
              </>
            )}
          </button>
        </div>

        {/* PREVIEW */}
        <div
          className="brutal p-5 bg-white lg:col-span-2 min-h-[420px] flex flex-col"
          data-testid="cert-designer-preview"
        >
          {preview ? (
            <>
              <div
                className="relative rounded overflow-hidden border-4 border-black shadow-xl"
                style={{ aspectRatio: "3/2" }}
              >
                <img
                  src={`data:${preview.mime_type};base64,${preview.data}`}
                  alt="certificate background"
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 flex flex-col items-center justify-center p-8 text-center pointer-events-none">
                  {proMode ? (
                    <div
                      className="px-[6%] py-[5%] rounded w-[88%]"
                      style={{ background: "rgba(255,255,255,0.55)", backdropFilter: "blur(2px)", border: "1px solid rgba(180,150,60,0.55)" }}
                      data-testid="cert-preview-professional"
                    >
                      <div
                        className="tracking-[0.18em]"
                        style={{ fontFamily: "Georgia, 'Times New Roman', serif", color: "#0d1a3f", fontWeight: 700, fontSize: "clamp(1rem,2.6vw,2.1rem)" }}
                      >
                        {lang === "es" ? "CERTIFICADO DE APRECIACIÓN" : "CERTIFICATE OF APPRECIATION"}
                      </div>
                      <div className="mx-auto mt-1 mb-2" style={{ width: "34%", height: 2, background: "#b4963c" }} />
                      <div className="tracking-[0.14em] uppercase text-neutral-600" style={{ fontSize: "clamp(.6rem,1.3vw,.8rem)" }}>
                        {lang === "es" ? "Este certificado se otorga con orgullo a" : "This certificate is proudly presented to"}
                      </div>
                      <div
                        className="mt-1"
                        style={{ fontFamily: fontReady ? "'GreatVibes', cursive" : "Georgia, serif", color: "#111", fontSize: "clamp(2.2rem,6vw,4.4rem)", lineHeight: 1.05 }}
                      >
                        {employeeName || (lang === "es" ? "Nombre Apellido" : "Name Surname")}
                      </div>
                      <div className="mx-auto mt-1 mb-2" style={{ width: "60%", height: 1, background: "rgba(0,0,0,0.35)" }} />
                      <div
                        className="tracking-[0.14em] uppercase"
                        style={{ fontFamily: "Georgia, serif", color: "#0d1a3f", fontWeight: 700, fontSize: "clamp(.7rem,1.5vw,1.1rem)" }}
                      >
                        {lang === "es" ? achievementEs : achievementEn}
                      </div>
                    </div>
                  ) : (
                    <div
                      className="bg-canales-cream/80 backdrop-blur px-6 py-6 rounded-md max-w-[80%]"
                      style={{ boxShadow: "0 10px 30px rgba(0,0,0,0.3)" }}
                      data-testid="cert-preview-fun"
                    >
                      <div
                        className="font-display font-black tracking-widest text-canales-navy"
                        style={{ fontSize: "clamp(1rem,2.4vw,2rem)", color: "#0d1a3f" }}
                      >
                        {lang === "es" ? "CERTIFICADO DE EXCELENCIA" : "CERTIFICATE OF EXCELLENCE"}
                      </div>
                      <div className="mt-2 text-xs sm:text-sm text-neutral-700">
                        {lang === "es" ? "Otorgado con orgullo a" : "Proudly presented to"}
                      </div>
                      <div
                        className="mt-1 italic font-black text-canales-red"
                        style={{ fontSize: "clamp(1.5rem,3.5vw,3rem)" }}
                      >
                        {employeeName}
                      </div>
                      <div
                        className="mt-2 font-display font-black tracking-widest"
                        style={{ color: "#0d1a3f", fontSize: "clamp(.8rem,1.5vw,1.2rem)" }}
                      >
                        {lang === "es" ? achievementEs : achievementEn}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  data-testid="cert-designer-download"
                  onClick={downloadPDF}
                  className="brutal-btn bg-canales-blue text-white px-4 py-2 flex items-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  {lang === "es" ? "Descargar PDF" : "Download PDF"}
                </button>
                <button
                  onClick={generate}
                  disabled={generating}
                  className="brutal-btn bg-white px-4 py-2 flex items-center gap-2"
                >
                  <Wand2 className="w-4 h-4" />
                  {lang === "es" ? "Regenerar" : "Regenerate"}
                </button>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-center text-neutral-500 font-body">
              <div>
                <Award className="w-16 h-16 mx-auto text-neutral-300" />
                <p className="mt-2">
                  {lang === "es"
                    ? "Elige un vendedor, un diseño y genera el certificado."
                    : "Pick a vendor, a design, and generate the certificate."}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* HISTORY */}
      {history.length > 0 && (
        <div className="brutal p-5 bg-white">
          <div className="flex items-center justify-between">
            <div className="font-display font-black text-lg">
              {lang === "es" ? "Últimos certificados generados" : "Recent certificates"}
            </div>
            <div className="text-xs text-neutral-500 font-body">
              {history.length} {lang === "es" ? "diseños guardados" : "designs stored"}
            </div>
          </div>
          <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {history.map((h) => (
              <div
                key={h.key}
                className="brutal-sm bg-canales-cream overflow-hidden"
                data-testid={`cert-history-${h.key}`}
              >
                <div className="relative aspect-[3/2] overflow-hidden">
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL || ""}/api/admin/certificate/${h.key}/image`}
                    alt=""
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-2 flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-display font-bold text-sm truncate">
                      {h.employee_name}
                    </div>
                    <div className="text-[10px] text-neutral-500 font-body truncate">
                      {h.template} · {new Date(h.generated_at).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button
                      onClick={() => {
                        setPreview(h);
                        setEmployeeName(h.employee_name);
                        setSelectedTpl(h.template);
                      }}
                      className="brutal-btn bg-canales-yellow text-black px-2 py-1 text-xs"
                      title={lang === "es" ? "Ver / descargar" : "View / download"}
                    >
                      <Download className="w-3 h-3" />
                    </button>
                    <button
                      onClick={() => deleteDesign(h.key)}
                      className="brutal-btn bg-white text-black px-2 py-1 text-xs"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
}
